import { useEffect, useRef } from "react";
import { ArrowDown, MapPin, CheckCircle2 } from "lucide-react";
import { HERO_IMAGE } from "@/data/apartments";
import { useT } from "@/i18n/LanguageProvider";
import { QualitySeal } from "./QualitySeal";

export const Hero = () => {
    const t = useT();
    const imgRef = useRef(null);

    // Subtle parallax: the hero image drifts slower than the scroll.
    useEffect(() => {
        let raf = 0;
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                if (imgRef.current) {
                    const y = Math.min(window.scrollY, window.innerHeight * 1.2) * 0.28;
                    imgRef.current.style.transform = `translateY(${y}px) scale(1.05)`;
                }
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section className="relative min-h-screen w-full overflow-hidden" data-testid="hero-section">
            <div className="absolute inset-0">
                <img
                    ref={imgRef}
                    src={HERO_IMAGE}
                    alt="G21 Wiesbaden-Biebrich – Vorderhaus"
                    className="w-full h-full object-cover will-change-transform"
                    style={{ transform: "translateY(0px) scale(1.05)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/85 via-stone-900/40 to-stone-900/30" />
                <div className="absolute inset-0 grain opacity-10 pointer-events-none" />
            </div>

            <QualitySeal />

            <div className="relative z-10 container-page flex flex-col justify-end min-h-screen pt-32 pb-32 sm:pb-36 lg:pb-44">
                <div className="reveal-up">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="inline-block w-12 h-px bg-clay-300" />
                        <span className="overline text-clay-200" data-testid="hero-eyebrow">
                            {t({ de: "Neubauvorhaben · Verkauf gestartet", en: "New build · Sales open" })}
                        </span>
                    </div>

                    <h1
                        className="h-display text-white text-balance max-w-[18ch] sm:max-w-none"
                        data-testid="hero-title"
                    >
                        {t({
                            de: <>Vier Eigentumswohnungen mit Charakter</>,
                            en: <>Four condominiums with character</>,
                        })}
                        <span className="block text-clay-300 italic font-normal mt-1">
                            {t({ de: "in Wiesbaden-Biebrich.", en: "in Wiesbaden-Biebrich." })}
                        </span>
                    </h1>

                    <div className="max-w-3xl">
                        <p className="body-lg text-white/85 mt-8 max-w-xl" data-testid="hero-subtitle">
                            {t({
                                de: "Modernes 3-Familienhaus mit Loft-Hinterhaus, Effizienzhaus 40, eigener Wärmepumpe je Wohnung und PKW-Carport im Hintergebäude. Ruhig wohnen, urban verbunden.",
                                en: "Modern 3-family home with rear-side loft, energy-efficient class 40 (Effizienzhaus 40), own heat pump per apartment and a covered carport in the rear building. Quiet living, urban connection.",
                            })}
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                onClick={() => scrollTo("wohnungen")}
                                className="btn-primary"
                                data-testid="hero-cta-apartments"
                            >
                                {t({ de: "Wohnungen entdecken", en: "Discover apartments" })}
                                <ArrowDown size={16} />
                            </button>
                            <button
                                onClick={() => scrollTo("lage")}
                                className="px-7 py-3.5 border border-white/40 text-white font-body font-medium tracking-wide transition-all hover:bg-white hover:text-ink"
                                data-testid="hero-cta-location"
                            >
                                {t({ de: "Über die Lage", en: "About the location" })}
                            </button>
                        </div>

                        <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/80 font-body text-sm">
                            <div className="flex items-center gap-2">
                                <MapPin size={16} className="text-clay-300" />
                                <span>Gaugasse 21, 65203 Wiesbaden</span>
                            </div>
                            <span className="hidden sm:inline-block w-px h-4 bg-white/30" />
                            <span>{t({ de: "Effizienzhaus 40", en: "Energy class 40 (EH 40)" })}</span>
                            <span className="hidden sm:inline-block w-px h-4 bg-white/30" />
                            <span
                                onClick={() => scrollTo("bauphase")}
                                className="group inline-flex items-center gap-2 px-3 py-1 bg-green-500/15 border border-green-400/40 text-green-300 cursor-pointer hover:bg-green-500/30 hover:border-green-300 hover:shadow-[0_0_16px_rgba(52,211,153,0.35)] transition-all"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); scrollTo("bauphase"); } }}
                                data-testid="hero-baugenehmigung-badge"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-70 animate-ping" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
                                </span>
                                <CheckCircle2 size={15} strokeWidth={2.2} className="text-green-400" />
                                <span className="font-medium tracking-wide">
                                    {t({ de: "Baugenehmigung erteilt", en: "Building permit granted" })}
                                </span>
                                <span className="ml-1 text-[11px] text-green-200/90 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {t({ de: "Bauphase ansehen →", en: "View build phase →" })}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-8 right-8 hidden md:flex items-center gap-3 text-white/70 font-body text-xs uppercase tracking-[0.2em]">
                <span>{t({ de: "Scrollen", en: "Scroll" })}</span>
                <span className="block w-12 h-px bg-white/40 animate-pulse" />
            </div>
        </section>
    );
};
